import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, AfterViewInit, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { EntityType } from '../../../../data/interfaces/circle-credit.interface';
import { CircleCreditService } from '../../../../data/services/view/circle-credit.service';

@Component({
  selector: 'app-circle-credit-edit-modal',
  templateUrl: './circle-credit-edit-modal.component.html',
  styleUrls: ['./circle-credit-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CircleCreditEditModalComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() visible: boolean = false;
  @Input() entityType: EntityType = 'Física';
  @Input() formGroup!: FormGroup;
  @Input() loading: boolean = false;
  
  @Output() onHide = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();

  activeTab: string = 'header';
  
  // Configuración de pestañas según el tipo de entidad
  get tabsConfig() {
    if (this.entityType === 'Física') {
      const pfTabs = [
        { key: 'header', label: 'Encabezado', icon: 'pi pi-info-circle' },
        { key: 'personalData', label: 'Datos Personales', icon: 'pi pi-user' },
        { key: 'address', label: 'Domicilio', icon: 'pi pi-map-marker' },
        { key: 'employment', label: 'Empleo', icon: 'pi pi-briefcase' },
        { key: 'accountDetail', label: 'Detalle Cuenta', icon: 'pi pi-credit-card' },
        { key: 'controlFigures', label: 'Cifras de Control', icon: 'pi pi-chart-bar' }
      ];
      return pfTabs;
    } else {
      const pmTabs = [
        { key: 'header', label: 'Encabezado', icon: 'pi pi-info-circle' },
        { key: 'company', label: 'Empresa', icon: 'pi pi-building' },
        { key: 'shareholder', label: 'Accionista', icon: 'pi pi-users' },
        { key: 'guarantor', label: 'Aval', icon: 'pi pi-shield' },
        { key: 'credits', label: 'Créditos', icon: 'pi pi-chart-line' }
      ];
      return pmTabs;
    }
  }

  constructor(
    private circleCreditService: CircleCreditService, 
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) { }

  // Getter para observar el estado de loading del service
  get isLoading(): boolean {
    return this.circleCreditService.isSaving$.value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando el formGroup cambia, ejecutar sincronización
    if (changes['formGroup'] && this.formGroup) {
      this.syncFieldsBasedOnEntityType();
      this.cdr.markForCheck();
    }
    
    // Cuando entityType cambia, reinicializar la pestaña activa
    if (changes['entityType']) {
      this.activeTab = this.tabsConfig[0].key;
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    if (!this.activeTab) this.activeTab = 'header';
    
    this.syncFieldsBasedOnEntityType();
  }

  ngAfterViewInit() {}

  ngOnDestroy(): void {}

  // Método temporal para debugging
  onTabDebugClick(event: Event, tabKey: string) {
    this.onTabChange(tabKey);
  }

  onTabChange(tabKey: string) {

    // Solo cambiar si es diferente al tab actual
    if (this.activeTab !== tabKey) {
      this.activeTab = tabKey;
      
      // Forzar detección de cambios inmediatamente
      this.cdr.markForCheck();
      
      // Opcional: Detectar cambios de forma síncrona para debugging
      this.cdr.detectChanges();
    }
  }

  onModalHide(): void {
    this.onHide.emit();
  }

  onModalShow(): void { }

  onSaveData(): void {
    // Prevenir guardado múltiple si ya está guardando
    if (this.isLoading) return;
    
    // 1. Sincronizar campos según tipo de entidad
    this.syncFieldsBasedOnEntityType();
    
    // 2. Construir estructura final para el backend
    const finalData = this.buildFinalDataStructure();
    
    if (this.formGroup.valid) {
      this.onSave.emit(finalData);
    } else {
      // Marcar todos los campos como touched para mostrar errores
      this.markFormGroupTouched(this.formGroup);
    }
  }

  /**
   * Construye la estructura final de datos según el tipo de entidad
   */
  private buildFinalDataStructure(): any {
    // La estructura del formGroup ya coincide exactamente con lo que espera el backend
    return this.formGroup.value;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getTabTitle(): string {
    const currentTab = this.tabsConfig.find(tab => tab.key === this.activeTab);
    const title = currentTab ? currentTab.label : 'Información';
    return title;
  }

  // Método auxiliar para debugging del template
  logTabRender(tab: any, index: number): any {
    return tab;
  }

  // TrackBy function para el *ngFor
  trackByTabKey(index: number, tab: any): string {
    return tab.key;
  }

  /**
   * Sincroniza campos según el tipo de entidad (PF o PM)
   */
  private syncFieldsBasedOnEntityType(): void {
    if (!this.formGroup) {
      console.warn('⚠️ FormGroup not available for sync');
      return;
    }
    
    try {
      if (this.entityType === 'Física') {
        this.syncPFFields();
      } else {
        this.syncPMFields();
      }
    } catch (error) {
      console.error('❌ Error during field synchronization:', error);
    }
    
    this.cdr.markForCheck();
  }

  /**
   * Sincronización específica para Persona Física
   */
  private syncPFFields(): void {
    if (!this.formGroup) return;
    
    // Template -> Estructura Backend
    const firstName = this.formGroup.get('firstName')?.value;
    const paternalLastName = this.formGroup.get('paternalLastName')?.value;
    const maternalLastName = this.formGroup.get('maternalLastName')?.value;
    const rfc = this.formGroup.get('rfc')?.value;
    const curp = this.formGroup.get('curp')?.value;
    const birthDate = this.formGroup.get('birthDate')?.value;
    const gender = this.formGroup.get('gender')?.value;
    const nationality = this.formGroup.get('nationality')?.value;
    const grantorName = this.formGroup.get('grantorName')?.value;

    // Actualizar estructura anidada para PF
    if (firstName) this.formGroup.get('personalData.givenNames')?.setValue(firstName, { emitEvent: false });
    if (paternalLastName) this.formGroup.get('personalData.lastNameFather')?.setValue(paternalLastName, { emitEvent: false });
    if (maternalLastName) this.formGroup.get('personalData.lastNameMother')?.setValue(maternalLastName, { emitEvent: false });
    if (rfc) this.formGroup.get('personalData.rfc')?.setValue(rfc, { emitEvent: false });
    if (curp) this.formGroup.get('personalData.curp')?.setValue(curp, { emitEvent: false });
    if (birthDate) this.formGroup.get('personalData.birthDate')?.setValue(birthDate, { emitEvent: false });
    if (gender) this.formGroup.get('personalData.sex')?.setValue(gender, { emitEvent: false });
    if (nationality) this.formGroup.get('personalData.nationality')?.setValue(nationality, { emitEvent: false });
    if (grantorName) this.formGroup.get('header.grantorName')?.setValue(grantorName, { emitEvent: false });

    // Backend -> Template (para carga inicial)
    const personalData = this.formGroup.get('personalData')?.value;
    const headerData = this.formGroup.get('header')?.value;
    
    if (personalData) {
      if (personalData.givenNames) this.formGroup.get('firstName')?.setValue(personalData.givenNames, { emitEvent: false });
      if (personalData.lastNameFather) this.formGroup.get('paternalLastName')?.setValue(personalData.lastNameFather, { emitEvent: false });
      if (personalData.lastNameMother) this.formGroup.get('maternalLastName')?.setValue(personalData.lastNameMother, { emitEvent: false });
      if (personalData.rfc) this.formGroup.get('rfc')?.setValue(personalData.rfc, { emitEvent: false });
      if (personalData.curp) this.formGroup.get('curp')?.setValue(personalData.curp, { emitEvent: false });
      if (personalData.birthDate) this.formGroup.get('birthDate')?.setValue(personalData.birthDate, { emitEvent: false });
      if (personalData.sex) this.formGroup.get('gender')?.setValue(personalData.sex, { emitEvent: false });
      if (personalData.nationality) this.formGroup.get('nationality')?.setValue(personalData.nationality, { emitEvent: false });
    }
    
    if (headerData && headerData.grantorName) {
      this.formGroup.get('grantorName')?.setValue(headerData.grantorName, { emitEvent: false });
    }
  }

  /**
   * Sincronización específica para Persona Moral
   */
  private syncPMFields(): void {
    if (!this.formGroup) return;
    
    // Template -> Estructura Backend
    const companyName = this.formGroup.get('companyName')?.value;
    const rfc = this.formGroup.get('rfc')?.value;
    const nationality = this.formGroup.get('nationality')?.value;
    const grantorName = this.formGroup.get('grantorName')?.value;

    // Actualizar estructura anidada para PM
    if (companyName) this.formGroup.get('company.companyName')?.setValue(companyName, { emitEvent: false });
    if (rfc) this.formGroup.get('company.rfc')?.setValue(rfc, { emitEvent: false });
    if (nationality) this.formGroup.get('company.nationality')?.setValue(nationality, { emitEvent: false });
    if (grantorName) this.formGroup.get('grantorName')?.setValue(grantorName, { emitEvent: false });

    // Backend -> Template (para carga inicial)
    const company = this.formGroup.get('company')?.value;
    const grantorNameFromRoot = this.formGroup.get('grantorName')?.value;
    
    if (company) {
      if (company.companyName) this.formGroup.get('companyName')?.setValue(company.companyName, { emitEvent: false });
      if (company.rfc) this.formGroup.get('rfc')?.setValue(company.rfc, { emitEvent: false });
      if (company.nationality) this.formGroup.get('nationality')?.setValue(company.nationality, { emitEvent: false });
    }
    
    if (grantorNameFromRoot) {
      this.formGroup.get('grantorName')?.setValue(grantorNameFromRoot, { emitEvent: false });
    }
  }

  /**
   * Verifica si un objeto tiene valores no vacíos
   */

  /**
   * Convierte arrays del template a objetos singulares para el backend (solo PF)
   */

  /**
   * Verifica si un objeto tiene valores no vacíos
   */
  private hasNonEmptyValues(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some(value => 
      value !== null && value !== undefined && value !== ''
    );
  }

  // ==================== MÉTODOS DE GESTIÓN DE CRÉDITOS ====================

  /**
   * Agrega un nuevo crédito al FormArray
   */
  addNewCredit(): void {
    this.circleCreditService.addNewCredit();
    this.cdr.markForCheck();
  }

  /**
   * Edita un crédito existente
   */
  editCredit(index: number): void {
    this.circleCreditService.editCredit(index);
    this.cdr.markForCheck();
  }

  /**
   * Elimina un crédito del FormArray
   */
  removeCredit(index: number): void {
    this.circleCreditService.removeCredit(index);
    this.cdr.markForCheck();
  }

  /**
   * Getter para acceder al FormArray de créditos
   */
  get creditsArray() {
    return this.circleCreditService.creditsArray;
  }

  /**
   * Método auxiliar para obtener el FormArray de créditos
   */
  getCreditsArray() {
    const credits = this.formGroup?.get('credits') as any;
    const result = credits || { controls: [] };

    return result;
  }

  /**
   * Método auxiliar para obtener el FormArray de detalles de un crédito específico
   */
  getCreditDetailsArray(creditIndex: number) {
    const creditsArray = this.formGroup?.get('credits') as any;
    if (!creditsArray || !creditsArray.at) return { controls: [] };
    
    const creditFormGroup = creditsArray.at(creditIndex);
    if (!creditFormGroup) return { controls: [] };
    
    const details = creditFormGroup.get('details') as any;
    return details || { controls: [] };
  }

  /**
   * Agrega un nuevo detalle a un crédito específico
   */
  addCreditDetail(creditIndex: number): void {
    this.circleCreditService.addCreditDetail(creditIndex);
    this.cdr.markForCheck();
  }

  /**
   * Elimina un detalle específico de un crédito
   */
  removeCreditDetail(creditIndex: number, detailIndex: number): void {
    this.circleCreditService.removeCreditDetail(creditIndex, detailIndex);
    this.cdr.markForCheck();
  }
}
